import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Loader2, Crown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { BRANCHES } from "@/lib/constants";

const EMPTY = { name: "", phone: "", email: "", password: "", permissions: ["view"], branches: [] };

const BranchPicker = ({ value, onChange, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {[["", "كل الفروع"], ...BRANCHES.map((b) => [b, b])].map(([b, l]) => {
      const on = b ? value.includes(b) : value.length === 0;
      const next = b ? (value.includes(b) ? value.filter((x) => x !== b) : [...value, b]) : [];
      return (
        <button type="button" key={l} onClick={() => onChange(next.length === BRANCHES.length ? [] : next)} data-testid={`${testId}-${b || "all"}`}
          className={`h-9 px-3 rounded-full border text-sm font-semibold ${on ? "bg-brand-navy text-white border-brand-navy" : "border-slate-200 hover:bg-slate-100"}`}>{l}</button>
      );
    })}
  </div>
);

const AdminDialog = ({ open, onClose, onSaved, perms }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(EMPTY);
  }, [open]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggle = (p) => setF((x) => ({ ...x, permissions: x.permissions.includes(p) ? x.permissions.filter((y) => y !== p) : [...x.permissions, p] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admins", { ...f, email: f.email.trim() || null });
      toast.success("تم إنشاء حساب المدير");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" data-testid="admin-dialog">
        <DialogHeader><DialogTitle className="text-start font-heading">مدير جديد</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>الاسم *</Label><Input required value={f.name} onChange={set("name")} data-testid="admin-name-input" /></div>
          <div className="space-y-1.5"><Label>رقم الجوال (للدخول)</Label><Input dir="ltr" className="text-left" placeholder="05xxxxxxxx" value={f.phone} onChange={set("phone")} data-testid="admin-phone-input" /></div>
          <div className="space-y-1.5"><Label>البريد الإلكتروني (اختياري)</Label><Input type="email" dir="ltr" className="text-left" value={f.email} onChange={set("email")} data-testid="admin-email-input" /></div>
          <div className="space-y-1.5"><Label>كلمة المرور * (6 أحرف على الأقل)</Label><Input required minLength={6} dir="ltr" className="text-left" value={f.password} onChange={set("password")} data-testid="admin-password-input" /></div>
          <div className="space-y-2">
            <Label>الصلاحيات</Label>
            {Object.entries(perms).map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={f.permissions.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`admin-new-perm-${k}`} />{l}
              </label>
            ))}
          </div>
          <div className="space-y-2"><Label>الفرع المسموح</Label><BranchPicker value={f.branches} onChange={(v) => setF((x) => ({ ...x, branches: v }))} testId="admin-new-branches" /></div>
          <button disabled={busy} data-testid="admin-submit-button" className="w-full h-12 rounded-lg bg-brand-navy text-white font-bold hover:bg-brand-navy-dark flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={18} className="animate-spin" />}إنشاء الحساب
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdminCard = ({ a, perms, onChange }) => {
  const [pwd, setPwd] = useState("");
  const update = async (body, msg) => {
    try {
      await api.put(`/admins/${a.id}`, body);
      toast.success(msg);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const togglePerm = (p) => update({ permissions: a.permissions.includes(p) ? a.permissions.filter((x) => x !== p) : [...a.permissions, p] }, "تم تحديث الصلاحيات");

  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 ${a.active ? "" : "opacity-60"}`} data-testid={`admin-card-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-bold text-lg flex items-center gap-2">{a.name}{a.is_owner && <Crown size={16} className="text-amber-500" />}</p>
          <p className="text-xs text-slate-500 font-mono truncate" dir="ltr">{a.phone || a.email}</p>
        </div>
        {!a.is_owner && <Switch checked={a.active} onCheckedChange={() => update({ active: !a.active }, a.active ? "تم إيقاف الحساب" : "تم تفعيل الحساب")} data-testid={`admin-active-switch-${a.id}`} />}
      </div>
      {a.is_owner ? (
        <p className="mt-4 text-sm text-brand-navy font-semibold">المدير الرئيسي: كل الصلاحيات + إدارة المدراء</p>
      ) : (
        <>
          <div className="mt-4 space-y-2">
            <p className="text-xs font-bold text-slate-500">الفرع</p>
            <BranchPicker value={a.branches || []} onChange={(v) => update({ branches: v }, "تم تحديث الفرع")} testId={`admin-branches-${a.id}`} />
          </div>
          <div className="mt-4 space-y-2">
            {Object.entries(perms).map(([k, l]) => (
              <label key={k} className="flex items-center justify-between gap-2 text-sm">
                <span>{l}</span>
                <Switch checked={a.permissions.includes(k)} onCheckedChange={() => togglePerm(k)} data-testid={`admin-perm-${a.id}-${k}`} />
              </label>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Input value={pwd} onChange={(e) => setPwd(e.target.value)} type="password" autoComplete="new-password" placeholder="كلمة مرور جديدة" dir="ltr" className="h-10 text-left" data-testid={`admin-new-password-${a.id}`} />
            <button onClick={() => pwd.length >= 6 ? update({ password: pwd }, "تم تغيير كلمة المرور").then(() => setPwd("")) : toast.error("6 أحرف على الأقل")}
              data-testid={`admin-set-password-${a.id}`} className="h-10 px-3 shrink-0 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-100">تغيير</button>
          </div>
        </>
      )}
    </div>
  );
};

export default function Admins() {
  const [data, setData] = useState({ admins: [], permissions: {} });
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/admins").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-8" data-testid="admins-page">
      <div className="flex flex-wrap items-end justify-between gap-4 rise">
        <div>
          <p className="text-xs font-bold text-brand-navy mb-1 flex items-center gap-1"><ShieldCheck size={14} />للمدير الرئيسي فقط</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">المدراء والصلاحيات</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-admin-button" className="h-12 px-5 rounded-lg bg-brand-red text-white font-bold flex items-center gap-2 hover:bg-brand-red-dark"><UserPlus size={18} /> إضافة مدير</button>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admins-list">
        {data.admins.map((a) => <AdminCard key={a.id} a={a} perms={data.permissions} onChange={load} />)}
      </div>
      <AdminDialog open={open} onClose={() => setOpen(false)} onSaved={load} perms={data.permissions} />
    </div>
  );
}
